import { useL } from "@/lib/useL";
import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Clock, Tag, MessageCircle, FileText, ArrowLeft, ChevronRight, HelpCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import PageSEO from "@/components/PageSEO";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import placeholderImg from "@/assets/essaouira-guide-placeholder.webp";
import { trackWhatsAppClick } from "@/lib/gtag";
import GuideArticleCard, { type GuideArticle as GuideArticleT } from "@/components/GuideArticleCard";
import { useAITranslate } from "@/hooks/useAITranslate";
import ArticleAuthorHeader from "@/components/ArticleAuthorHeader";

interface ArticleImage {
  id: string;
  slot: number;
  image_url: string;
  alt_text: string;
}

const WHATSAPP_NUMBER = "212706280215";

const GuideArticle = () => {
  const { t } = useTranslation();
  const L = useL();
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<GuideArticleT | null>(null);
  const [images, setImages] = useState<ArticleImage[]>([]);
  const [others, setOthers] = useState<GuideArticleT[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  // AI-translated editorial fields (cached in localStorage). Reviews excluded.
  const [tTitle, tIntro, tBody] = useAITranslate([
    article?.title || "",
    article?.intro || "",
    article?.body || "",
  ], "en");
  const [
    tLoading,
    tNotFoundTitle,
    tNotFoundBody,
    tBackToGuide,
    tBreadcrumbLabel,
    tHome,
    tGuideLink,
  ] = useAITranslate([
    "Loading…",
    "Article not found",
    "This article does not exist or has been moved.",
    "Back to guide",
    "Breadcrumb",
    "Home",
    "Essaouira Guide",
  ], "en");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    let alive = true;
    (async () => {
      setLoading(true);
      const { data: art } = await (supabase as any)
        .from("guide_articles")
        .select("*")
        .eq("slug", slug)
        .maybeSingle();
      if (!alive) return;
      setArticle(art || null);
      if (art) {
        const [{ data: imgs }, { data: rest }] = await Promise.all([
          (supabase as any)
            .from("guide_article_images")
            .select("*")
            .eq("article_id", art.id)
            .order("slot", { ascending: true }),
          (supabase as any)
            .from("guide_articles")
            .select("*")
            .neq("id", art.id)
            .order("slot", { ascending: true })
            .limit(3),
        ]);
        if (!alive) return;
        setImages(imgs || []);
        setOthers(rest || []);
      }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [slug]);

  const slides = images.length
    ? images.map((i) => ({ src: i.image_url, alt: i.alt_text || article?.title || "" }))
    : [{ src: placeholderImg, alt: article?.title || "Excursion" }];

  const waLink = article
    ? `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
        t("guideArticle.waMessage", { title: article.title, duration: article.duration, price: article.price }),
      )}`
    : "#";

  const handleBookViaForm = () => {
    if (!article) return;
    setDialogOpen(false);
    // Navigate home then dispatch after route renders the form
    const trigger = () => {
      window.dispatchEvent(new CustomEvent("select-service", { detail: "excursion" }));
      window.dispatchEvent(
        new CustomEvent("prefill-message", {
          detail: t("guideArticle.waMessage", { title: article.title, duration: article.duration, price: article.price }),
        }),
      );
      setTimeout(() => {
        document.getElementById("booking")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    };
    // If form lives on /, go there with hash; the booking section listens for events
    window.location.assign(`/#booking`);
    setTimeout(trigger, 300);
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-6 py-24 text-center text-muted-foreground">{tLoading}</div>
      </main>
    );
  }

  if (!article) {
    return (
      <main className="min-h-screen bg-background">
        <PageSEO title={`${tNotFoundTitle} — Maroc Transfers`} description={tNotFoundBody} path={`/guide/${slug}`} noindex />
        <Navbar />
        <div className="container mx-auto px-6 py-24 text-center space-y-4">
          <h1 className="font-heading text-3xl font-bold">{tNotFoundTitle}</h1>
          <p className="text-muted-foreground">{tNotFoundBody}</p>
          <Button asChild>
            <Link to="/#guide-essaouira"><ArrowLeft className="mr-2 h-4 w-4" />{tBackToGuide}</Link>
          </Button>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background">
      <PageSEO
        title={`${article.title.length > 40 ? article.title.slice(0, 37) + "…" : article.title} | Maroc Transfers`}
        description={article.intro.slice(0, 158)}
        path={`/guide/${article.slug}`}
        ogType="article"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.intro,
            image: images.map((i) => i.image_url),
            url: `https://maroctransfers.com/guide/${article.slug}`,
            author: { "@type": "Organization", name: "Maroc Transfers" },
            publisher: { "@type": "Organization", name: "Maroc Transfers" },
          })}
        </script>
        <script type="application/ld+json" id="guide-faq-jsonld">
          {(() => {
            const sections = (t("guideFaq.sections", { returnObjects: true }) as { heading: string; items: { q: string; a: string }[] }[]) || [];
            const allQuestions = sections.flatMap((s) => s.items);
            return JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: allQuestions.map((item) => ({
                "@type": "Question",
                name: item.q,
                acceptedAnswer: { "@type": "Answer", text: item.a },
              })),
            });
          })()}
        </script>
      </Helmet>
      <Navbar />

      <article className="pt-28 pb-20">
        <div className="container mx-auto max-w-4xl px-6">
          <nav className="flex items-center gap-1 text-sm text-muted-foreground font-body mb-6" aria-label={tBreadcrumbLabel}>
            <Link to="/" className="hover:text-primary">{tHome}</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <Link to="/#guide-essaouira" className="hover:text-primary">{tGuideLink}</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-foreground truncate">{tTitle}</span>
          </nav>

          <header className="space-y-4 mb-8">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground">{tTitle}</h1>
            <ArticleAuthorHeader
              authorId={(article as any).author_id}
              publishedAt={(article as any).published_at || (article as any).created_at}
            />
            <div className="flex flex-wrap gap-2">
              {article.duration && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-sm text-secondary-foreground">
                  <Clock className="h-4 w-4" /> {article.duration}
                </span>
              )}
              {article.price && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
                  <Tag className="h-4 w-4" /> {article.price}
                </span>
              )}
            </div>
            <p className="font-body text-lg text-muted-foreground leading-relaxed">{tIntro}</p>
          </header>

          <div className="overflow-hidden rounded-2xl border border-border bg-card mb-10">
            <Carousel opts={{ loop: slides.length > 1 }} className="w-full">
              <CarouselContent>
                {slides.map((s, idx) => (
                  <CarouselItem key={idx}>
                    <img
                      src={s.src}
                      alt={s.alt}
                      loading={idx === 0 ? "eager" : "lazy"}
                      decoding="async"
                      className="h-72 w-full object-cover sm:h-[28rem]"
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
              {slides.length > 1 && (
                <>
                  <CarouselPrevious className="left-3" />
                  <CarouselNext className="right-3" />
                </>
              )}
            </Carousel>
          </div>

          <div className="font-body text-foreground leading-relaxed whitespace-pre-line text-base md:text-lg">
            {tBody}
          </div>

          <div className="mt-10 rounded-2xl border border-border bg-card p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="font-heading text-xl font-bold">{t("guideArticle.readyToBook")}</p>
              <p className="font-body text-sm text-muted-foreground">{article.duration} — {article.price}</p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button size="lg" className="bg-gradient-gold text-primary-foreground font-body">
                  {t("guideArticle.bookNow")}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading">{t("guideArticle.howToBook")}</DialogTitle>
                  <DialogDescription className="font-body">
                    {tTitle} — {article.duration} — {article.price}
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-3 pt-2">
                  <Button onClick={handleBookViaForm} className="w-full font-body">
                    <FileText className="mr-2 h-4 w-4" />
                    {t("guideArticle.bookViaForm")}
                  </Button>
                  <Button asChild className="w-full bg-[#25D366] text-white hover:bg-[#1ebe5a] font-body">
                    <a
                      href={waLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => { trackWhatsAppClick(`guide_article_${article.slug}`); setDialogOpen(false); }}
                    >
                      <MessageCircle className="mr-2 h-4 w-4" />
                      {t("guideArticle.contactWhatsApp")}
                    </a>
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Enhanced FAQ Section */}
          <section className="mt-16" aria-labelledby="guide-faq-heading">
            <div className="flex items-center gap-2 mb-6">
              <HelpCircle className="h-5 w-5 text-primary" />
              <h2 id="guide-faq-heading" className="font-heading text-2xl md:text-3xl font-bold text-foreground">
                {t("guideFaq.title")}
              </h2>
            </div>
            <p className="font-body text-muted-foreground mb-8">{t("guideFaq.subtitle")}</p>
            {(() => {
              const sections = (t("guideFaq.sections", { returnObjects: true }) as { heading: string; items: { q: string; a: string }[] }[]) || [];
              let globalIndex = 0;
              return (
                <div className="space-y-8">
                  {sections.map((section, sIdx) => (
                    <div key={sIdx}>
                      <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                        <span className="inline-block w-2 h-2 rounded-full bg-primary" />
                        {section.heading}
                      </h3>
                      <Accordion type="single" collapsible className="space-y-3">
                        {section.items.map((item) => {
                          const val = `gfq-${globalIndex++}`;
                          return (
                            <AccordionItem
                              key={val}
                              value={val}
                              className="bg-card border border-border rounded-xl px-4 md:px-5"
                            >
                              <AccordionTrigger className="font-heading text-left text-foreground hover:no-underline text-sm md:text-base py-4">
                                {item.q}
                              </AccordionTrigger>
                              <AccordionContent className="font-body text-muted-foreground leading-relaxed text-sm md:text-base">
                                {item.a}
                              </AccordionContent>
                            </AccordionItem>
                          );
                        })}
                      </Accordion>
                    </div>
                  ))}
                </div>
              );
            })()}
          </section>

          {others.length > 0 && (
            <section className="mt-16 space-y-6">
              <h2 className="font-heading text-2xl font-bold">{L('Other excursions', 'Autres excursions')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {others.map((o) => (
                  <GuideArticleCard key={o.id} article={o} />
                ))}
              </div>
            </section>
          )}
        </div>
      </article>

      <Footer />
      <WhatsAppButton />
    </main>
  );
};

export default GuideArticle;